import React, { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import { X, MessageSquare } from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { ConversationHistoryDrawer } from './ConversationHistoryDrawer';
import { useCurrentUser } from '@lingaai/user-management';
import { ChatPanel } from './ChatPanel';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface AgentPanelProps {
  children?: ReactNode;
}

export function AgentPanel({}: AgentPanelProps) {
  const { agentPanelWidth, setAgentPanelWidth, setAgentPanelOpen } = useUIStore();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState<{ id: string; title: string; updatedAt: Date }[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [loadConversationId, setLoadConversationId] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: user } = useCurrentUser();

  // Fetch conversations when drawer opens
  useEffect(() => {
    if (historyOpen && user?.id) {
      fetch(`${API_URL}/crm/chat/conversations?user_id=${user.id}`)
        .then((res) => res.json())
        .then((data) => {
          const formatted = data.map((conv: any) => ({
            id: conv.id,
            title: conv.title || 'Untitled',
            updatedAt: new Date(conv.updated_at),
          }));
          setConversations(formatted);
        })
        .catch((error) => console.error('Failed to load conversations:', error));
    }
  }, [historyOpen, user?.id]);

  const handleSelectConversation = (id: string) => {
    setLoadConversationId(id);
    setHistoryOpen(false);
  };

  const handleNewConversation = () => {
    setLoadConversationId(null);
    setCurrentConversationId(null);
    setResetKey((prev) => prev + 1);
    setHistoryOpen(false);
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return;
        const containerWidth = window.innerWidth;
        const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100;
        setAgentPanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [setAgentPanelWidth]
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        width: `${agentPanelWidth}%`,
        minWidth: '300px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      {/* Drag Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          cursor: 'col-resize',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'primary.main',
          },
          transition: 'background-color 0.2s',
          zIndex: 10,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">CRM Manager</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Conversation History">
            <IconButton size="small" onClick={() => setHistoryOpen(true)}>
              <MessageSquare size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setAgentPanelOpen(false)}>
            <X size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <ChatPanel loadConversationId={loadConversationId} resetKey={resetKey} onConversationIdChange={setCurrentConversationId} />
      </Box>

      {/* Conversation History Drawer */}
      <ConversationHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        conversations={conversations}
        currentConversationId={currentConversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
      />
    </Box>
  );
}
